<!--
INDICADOR DO DASHBOARD - NOTA BRASIL ERP
Baseado no Design System Stitch (Fiscal Precision Modern)
-->

<template>
<div class="mb-4" :class="columns" v-if="show">
    <div class="nb-dash-card" @click="handleClickCard">
        <!-- CARD HEADER -->
        <div class="nb-dash-header">
            <div class="d-flex align-items-center">
                <span class="nb-dash-title-text">{{ title }}</span>
                <i v-if="help" class="fa fa-info-circle nb-info-icon ml-2" data-toggle="tooltip" data-placement="top" :title="help" aria-hidden="true"></i>
            </div>
            <div class="d-flex align-items-center">
                <span class="nb-badge-mes" v-if="labelMes">
                    <i class="fa fa-calendar-o mr-1"></i> {{ labelMes }}
                </span>
            </div>
        </div>

        <!-- INDICADOR HORIZONTAL (KPIs) -->
        <div class="row p-3" v-if="params.indicator && show">
            <div class="col-lg-3 col-sm-6 col-12 mb-3" v-for="(value, index) in values" :key="index">
                <div class="dash-kpi-card" :class="'kpi-accent-' + (index % 4)">
                    <div class="kpi-top-bar"></div>
                    <div class="p-3 d-flex flex-column justify-content-between h-100">
                        <div class="d-flex justify-content-between align-items-start">
                            <span class="kpi-title">{{ value.title }}</span>
                            <div class="kpi-icon-wrapper" :class="'kpi-icon-bg-' + (index % 4)">
                                <i :class="getKpiIcon(index)" aria-hidden="true"></i>
                            </div>
                        </div>
                        <div class="mt-2">
                            <span class="kpi-value" v-if="formatValue">R$ {{ value.value | format }}</span>
                            <span class="kpi-value" v-if="!formatValue">{{ value.value }}</span>
                        </div>
                        <!-- MINI GRÁFICO DE EVOLUÇÃO (SPARKLINE) -->
                        <div class="kpi-bottom-row mt-2 d-flex align-items-center justify-content-between">
                            <div class="kpi-trend" :class="getTrendClass(index, value)">
                                <i :class="getTrendIcon(index, value)" class="mr-1"></i>
                                <span>{{ getTrendText(index, value) }}</span>
                            </div>
                            <svg class="kpi-sparkline" viewBox="0 0 100 30" preserveAspectRatio="none">
                                <defs>
                                    <linearGradient :id="'sparkGrad-' + index" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" :stop-color="getSparkColor(index)" stop-opacity="0.25" />
                                        <stop offset="100%" :stop-color="getSparkColor(index)" stop-opacity="0.0" />
                                    </linearGradient>
                                </defs>
                                <path :fill="'url(#sparkGrad-' + index + ')'" :d="getSparkAreaPath(index, value)" />
                                <path fill="none" :stroke="getSparkColor(index)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" :d="getSparkLinePath(index, value)" />
                            </svg>
                        </div>

                        <!-- DETALHAMENTO CONTEXTUAL / BADGES DE IMPOSTOS OU METADATA (SEMPRE EXIBIDO) -->
                        <div class="kpi-meta-footer mt-2 pt-2">
                            <!-- Badges de Impostos (Card 4) -->
                            <div class="d-flex align-items-center justify-content-between flex-wrap gap-1" v-if="value.breakdown">
                                <span class="badge badge-tax-icms" :title="'ICMS: R$ ' + formatMoney(value.breakdown.icms)">ICMS <strong>{{ formatShort(value.breakdown.icms) }}</strong></span>
                                <span class="badge badge-tax-pis" :title="'PIS/COFINS: R$ ' + formatMoney(value.breakdown.piscofins)">PIS/COF <strong>{{ formatShort(value.breakdown.piscofins) }}</strong></span>
                                <span class="badge badge-tax-ipi" :title="'IPI: R$ ' + formatMoney(value.breakdown.ipi)">IPI <strong>{{ formatShort(value.breakdown.ipi) }}</strong></span>
                            </div>
                            <!-- Subtexto de Quantidade (Cards 1, 2, 3 e outros) -->
                            <div class="d-flex align-items-center text-muted font-11" v-else>
                                <span><i class="fa fa-file-text-o mr-1"></i>{{ getDocCount(value) }} doc(s)</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- GRAFICO -->
        <div class="p-3 chart-wrapper" v-if="params.chart && show && !mensagem">
            <GChart :type="params.chart.type" :data="values" :options="chartOptions"></GChart>
        </div>

        <!-- MENSAGEM / EMPTY STATE -->
        <div class="row align-items-center p-5 d-flex" style="flex-grow: 1" v-if="params.chart && mensagem">
            <div class="col text-center empty-state-box">
                <i class="fa fa-bar-chart-o mb-2 text-muted" style="font-size: 28px; opacity: 0.5;"></i>
                <div class="text-muted font-weight-medium">{{ mensagem }}</div>
            </div>
        </div>

        <!-- DETALHES / FOOTER -->
        <div v-if="params.detalhes" class="nb-dash-footer">
            <a href="javascript:void(0);" @click.stop="handleDetalhes" class="nb-footer-link">
                <span>Ver relatório detalhado</span>
                <i class="fa fa-arrow-right ml-1" aria-hidden="true"></i>
            </a>
        </div>
    </div>
</div>
</template>

<script>
import { apiORDSpost } from '../shared/api-functions'
import { formatValue } from '../shared/utils'
import { GChart } from 'vue-google-charts/legacy'

export default {
    name: 'dash-card',

    props: {
      params: { type: Object },
      filter: { type: Object },
    },

    components: {
        GChart
    },

    data () {
        return {
            values: [],
            lineOptions: {
                curveType: 'function',
                lineWidth: 3,
                pointSize: 6,
                colors: ['#004e94', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'],
                height: 290,
                backgroundColor: { fill: 'transparent', strokeWidth: 0 },
                chartArea: { top: 30, width: '92%', height: '72%' },
                legend: { position: 'top', alignment: 'center', textStyle: { color: '#475569', fontSize: 12, fontName: 'Inter, sans-serif' } },
                tooltip: { textStyle: { color: '#0f172a', fontSize: 12, fontName: 'Inter, sans-serif' } },
                vAxis: {
                    gridlines: { color: '#f1f5f9' },
                    baselineColor: '#cbd5e1',
                    textStyle: { color: '#64748b', fontSize: 11, fontName: 'Inter, sans-serif' }
                },
                hAxis: {
                    gridlines: { color: 'transparent' },
                    baselineColor: '#cbd5e1',
                    textStyle: { color: '#64748b', fontSize: 11, fontName: 'Inter, sans-serif' }
                }
            },

            columnOptions: {
                isStacked: false,
                bar: { groupWidth: '62%' },
                colors: ['#004e94', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6'],
                height: 290,
                backgroundColor: { fill: 'transparent', strokeWidth: 0 },
                chartArea: { top: 30, width: '92%', height: '72%' },
                legend: { position: 'top', alignment: 'center', textStyle: { color: '#475569', fontSize: 12, fontName: 'Inter, sans-serif' } },
                tooltip: { textStyle: { color: '#0f172a', fontSize: 12, fontName: 'Inter, sans-serif' } },
                vAxis: {
                    gridlines: { color: '#f1f5f9' },
                    baselineColor: '#cbd5e1',
                    textStyle: { color: '#64748b', fontSize: 11, fontName: 'Inter, sans-serif' }
                },
                hAxis: {
                    gridlines: { color: 'transparent' },
                    baselineColor: '#cbd5e1',
                    textStyle: { color: '#64748b', fontSize: 11, fontName: 'Inter, sans-serif' }
                }
            },

            pieOptions: {
                isStacked: true,
                colors: ['#004e94', '#10b981', '#0ea5e9', '#f59e0b', '#8b5cf6', '#ef4444'],
                is3D: false,
                pieHole: 0.65,
                height: 290,
                showRowNumber: true,
                sliceVisibilityThreshold: 0,
                backgroundColor: { fill: 'transparent', strokeWidth: 0 },
                chartArea: { top: 30, width: '92%', height: '75%' },
                pieSliceText: 'percentage',
                pieSliceTextStyle: { color: '#ffffff', fontSize: 11, fontName: 'Inter, sans-serif' },
                legend: { position: 'top', alignment: 'center', maxLines: 2, textStyle: { color: '#475569', fontSize: 11, fontName: 'Inter, sans-serif' } },
                tooltip: { textStyle: { color: '#0f172a', fontSize: 12, fontName: 'Inter, sans-serif' } }
            },

            help: '',
            labelMes: '',
            title: '',
            show: false,
            formatValue: false,
            mensagem: null,
            data_inicio: null,
            data_termino: null,
        }
    },

    computed: {
        columns: function () {
            return 'col-md-' + (this.params.col || 12) + ' col-12'
        },

        chartOptions: function () {
            const chartType = this.params.chart ? this.params.chart.type : ''
            if (chartType === 'PieChart') {
                return this.pieOptions
            } else if (chartType === 'LineChart' || chartType === 'AreaChart') {
                return this.lineOptions
            } else {
                return this.columnOptions
            }
        },
    },

    watch: {
        filter: {
            handler() {
                if (this.filter) {
                    this.loadData()
                }
            },
            deep: true
        },
    },

    methods: {
        getKpiIcon(index) {
            const icons = [
                'fa fa-money',
                'fa fa-shopping-cart',
                'fa fa-line-chart',
                'fa fa-check-circle-o'
            ]
            return icons[index % icons.length]
        },

        handleDetalhes () {
            let filtro = {
                data_inicio: this.filter.startDate,
                data_termino: this.filter.endDate,
            }
            if (this.filter.codusuario)
              filtro.usuario = this.filter.codusuario

            if (this.data_inicio) {
                filtro.data_inicio = this.data_inicio
                filtro.data_termino = this.data_termino
            }

            this.$router.push({ path: this.params.detalhes.url, query: filtro })
        },

        async loadData() {
            if (!this.params.name)
              return

            let filtro = {
                data_inicio: this.filter.startDate,
                data_termino: this.filter.endDate,
            }
            if (this.filter.codusuario) {
              filtro.usuario = [ this.filter.codusuario ]
              filtro.codusuario = this.filter.codusuario
            }
            const data = await apiORDSpost(this.$store, 'dashboard/element/' + this.params.name, filtro, 'post')
            if (data) {
                this.values = data.data
                this.labelMes = data.mes
                this.title = data.title
                this.help = data.help
                this.formatValue = data.formatvalue
                this.mensagem = data.mensagem
                this.data_inicio = data.data_inicio
                this.data_termino = data.data_termino
            } else {
                this.values = []
            }

            this.show = true

            this.$nextTick(function () {
                jQuery('[data-toggle="tooltip"]').tooltip()
            })
        },

        getSparkColor(index) {
            const colors = ['#004e94', '#f59e0b', '#10b981', '#0ea5e9']
            return colors[index % colors.length]
        },

        getSparkLinePath(index, item) {
            if (item && item.history && Array.isArray(item.history) && item.history.length > 1) {
                const data = item.history
                const max = Math.max(...data)
                const min = Math.min(...data)
                const range = max === min ? 1 : max - min
                const count = data.length

                let path = ''
                for (let i = 0; i < count; i++) {
                    const x = (i / (count - 1)) * 100
                    const y = 26 - ((data[i] - min) / range) * 20
                    if (i === 0) {
                        path += `M ${x.toFixed(1)} ${y.toFixed(1)}`
                    } else {
                        const prevX = ((i - 1) / (count - 1)) * 100
                        const prevY = 26 - ((data[i - 1] - min) / range) * 20
                        const midX = (prevX + x) / 2
                        path += ` C ${midX.toFixed(1)} ${prevY.toFixed(1)}, ${midX.toFixed(1)} ${y.toFixed(1)}, ${x.toFixed(1)} ${y.toFixed(1)}`
                    }
                }
                return path
            }

            const paths = [
                'M0 24 Q 25 22, 40 14 T 70 8 T 100 3',
                'M0 26 Q 20 18, 45 19 T 75 10 T 100 5',
                'M0 20 Q 30 28, 50 12 T 80 16 T 100 4',
                'M0 15 Q 25 8, 50 20 T 75 12 T 100 8'
            ]
            return paths[index % paths.length]
        },

        getSparkAreaPath(index, item) {
            const linePath = this.getSparkLinePath(index, item)
            return linePath + ' L 100 30 L 0 30 Z'
        },

        getTrendClass(index, item) {
            if (item && typeof item.change === 'number') {
                if (item.change > 0) return 'trend-positive'
                if (item.change < 0) return 'trend-negative'
                return 'trend-neutral'
            }
            return 'trend-neutral'
        },

        getTrendIcon(index, item) {
            if (item && typeof item.change === 'number') {
                if (item.change > 0) return 'fa fa-arrow-up'
                if (item.change < 0) return 'fa fa-arrow-down'
                return 'fa fa-minus'
            }
            return 'fa fa-minus'
        },

        getTrendText(index, item) {
            if (item && typeof item.change === 'number') {
                const prefix = item.change > 0 ? '+' : ''
                return `${prefix}${item.change.toFixed(1).replace('.', ',')}% vs ant.`
            }
            return '0,0% vs ant.'
        },

        getDocCount(item) {
            if (item && item.qtd !== undefined && item.qtd !== null && !isNaN(item.qtd)) {
                return item.qtd
            }
            return 0
        },

        formatMoney(val) {
            if (val === null || val === undefined || isNaN(val)) return '0,00'
            return formatValue(val, 2)
        },

        formatShort(val) {
            const num = (val === null || val === undefined || isNaN(val)) ? 0 : Number(val)
            if (num >= 1000000) {
                return (num / 1000000).toFixed(1).replace('.', ',') + 'M'
            }
            if (num >= 1000) {
                return (num / 1000).toFixed(1).replace('.', ',') + 'k'
            }
            return formatValue(num, 2)
        },

        handleClickCard () {
            this.$emit('click', this.params)
        },

        handleButtonClick() {
            this.$emit('buttonclick')
        },
    },

    filters: {
        format: function (value) {
            return formatValue(value, 2)
        },
    },

    created () {
        this.loadData()
    }
}
</script>

<style scoped>
.nb-dash-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nb-dash-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.nb-dash-header {
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
}

.nb-dash-title-text {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #0b1c30;
  letter-spacing: -0.2px;
}

.nb-info-icon {
  color: #94a3b8;
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s;
}

.nb-info-icon:hover {
  color: #004e94;
}

.nb-badge-mes {
  font-size: 11px;
  font-weight: 600;
  color: #004e94;
  background: #eff4ff;
  border: 1px solid #dce9ff;
  padding: 3px 9px;
  border-radius: 20px;
  letter-spacing: 0.2px;
}

/* KPI CARDS */
.dash-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
  min-height: 100px;
  transition: all 0.2s ease;
}

.dash-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.kpi-top-bar {
  height: 3px;
  width: 100%;
}

.kpi-accent-0 .kpi-top-bar { background: #004e94; }
.kpi-accent-1 .kpi-top-bar { background: #f59e0b; }
.kpi-accent-2 .kpi-top-bar { background: #10b981; }
.kpi-accent-3 .kpi-top-bar { background: #0ea5e9; }

.kpi-title {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.kpi-value {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0b1c30;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.kpi-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.kpi-icon-bg-0 { background: #eff4ff; color: #004e94; }
.kpi-icon-bg-1 { background: #fffbeb; color: #d97706; }
.kpi-icon-bg-2 { background: #ecfdf5; color: #059669; }
.kpi-icon-bg-3 { background: #f0f9ff; color: #0284c7; }

/* SPARKLINE & TREND */
.kpi-sparkline {
  width: 78px;
  height: 26px;
  flex-shrink: 0;
}

.kpi-trend {
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 6px;
  letter-spacing: 0.2px;
}

.trend-positive {
  background: #ecfdf5;
  color: #059669;
}

.trend-negative {
  background: #fef2f2;
  color: #dc2626;
}

.trend-warning {
  background: #fffbeb;
  color: #d97706;
}

.trend-neutral {
  background: #eff4ff;
  color: #004e94;
}

.kpi-meta-footer {
  border-top: 1px dashed #e2e8f0;
}

.font-11 {
  font-size: 11px;
}

.badge-tax-icms {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 4px;
}

.badge-tax-pis {
  background: #f0f9ff;
  color: #0369a1;
  border: 1px solid #bae6fd;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 4px;
}

.badge-tax-ipi {
  background: #faf5ff;
  color: #7e22ce;
  border: 1px solid #e9d5ff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 4px;
}

/* CHARTS */
.chart-wrapper {
  width: 100%;
  min-height: 290px;
}

.empty-state-box {
  padding: 30px;
}

/* FOOTER */
.nb-dash-footer {
  padding: 10px 20px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  text-align: right;
}

.nb-footer-link {
  font-size: 12px;
  font-weight: 600;
  color: #004e94;
  text-decoration: none !important;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
}

.nb-footer-link:hover {
  color: #00376c;
}
</style>





// WEBPACK FOOTER //
// src/dashboards/dash-card.vue